import { useState } from 'react';
import {
    Area,
    Bar,
    BarChart,
    AreaChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import { getCategoryColor } from '../../../utils/colorUtils';
import {
    CashInCashOut, CashSource,
    ForecastRecord,
    CashOutlook8wRecord, CashOutlook13wRecord, TopExpensesResponse, TransactionFilterParams
} from '../../../api/types'
import WeeklyDrilldownModal from '../../margin/components/WeeklyDrilldownModal';

interface ChartsProps {
    forecastData?: ForecastRecord[];
    cashData?: CashInCashOut;
    cashOutlookData?: CashOutlook13wRecord[] | CashOutlook8wRecord[];
    topExpensesData?: TopExpensesResponse;
    cashSourcesData?: CashSource[];
    rawData?: any;
    daysFilter?: string;
    businessId?: string;
}

const parseLocalDate = (value?: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const formatWeekRange = (weekStart?: string, weekEnd?: string): string => {
    const start = parseLocalDate(weekStart);
    const end = parseLocalDate(weekEnd);
    if (!start || !end) return '';
    return `${start.getMonth() + 1}/${start.getDate()}-${end.getMonth() + 1}/${end.getDate()}`;
};

const formatCashOutlookTick = (point?: Partial<CashOutlook8wRecord> | null, fallbackValue?: string): string => {
    const startDate = parseLocalDate(point?.week_start ?? fallbackValue);
    if (startDate) return `${startDate.getMonth() + 1}/${startDate.getDate()}`;

    const endingDate = parseLocalDate(point?.week_ending);
    if (endingDate) return `${endingDate.getMonth() + 1}/${endingDate.getDate()}`;

    return point?.week_label || fallbackValue || '';
};

const formatCashOutlookTooltipTitle = (point: Partial<CashOutlook8wRecord>, fallbackValue?: string): string => {
    const range = formatWeekRange(point.week_start ?? fallbackValue, point.week_ending);
    return range ? `Week of ${range}` : `Week of ${formatCashOutlookTick(point, fallbackValue)}`;
};

const startOfWeekMonday = (value: Date): Date => {
    const start = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const offset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - offset);
    return start;
};

const addDays = (value: Date, days: number): Date => {
    const next = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    next.setDate(next.getDate() + days);
    return next;
};

const firstDisplayWeekStartOfMonth = (value: Date): Date => {
    const monthStart = new Date(value.getFullYear(), value.getMonth(), 1);
    const weekday = monthStart.getDay();
    const mondayIndex = weekday === 0 ? 6 : weekday - 1;
    const daysToFirstMonday = mondayIndex === 0 ? 0 : 7 - mondayIndex;
    return addDays(monthStart, daysToFirstMonday);
};

const formatDateKey = (value: Date): string => {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getCurrentNextMonthWindow = (asOfDateValue?: string) => {
    const asOfDate = parseLocalDate(asOfDateValue) ?? new Date();
    const normalizedAsOfDate = new Date(asOfDate.getFullYear(), asOfDate.getMonth(), asOfDate.getDate());
    const currentMonthStart = new Date(normalizedAsOfDate.getFullYear(), normalizedAsOfDate.getMonth(), 1);
    const currentWeekStart = startOfWeekMonday(normalizedAsOfDate);
    const monthWindowStart = firstDisplayWeekStartOfMonth(normalizedAsOfDate);
    const endOfNextMonth = new Date(normalizedAsOfDate.getFullYear(), normalizedAsOfDate.getMonth() + 2, 0);

    return {
        asOfDate: normalizedAsOfDate,
        currentMonthStart,
        currentWeekStart,
        monthWindowStart,
        endOfNextMonth,
    };
};

/**
 * Scaled window for 60/90 day scope: extends history and forecast proportionally.
 * For 30 days: ~4 weeks history + ~4 weeks forecast (current+next month).
 * For 60 days: ~8 weeks history + ~8 weeks forecast.
 * For 90 days: ~13 weeks history + ~13 weeks forecast.
 */
const getScaledWindow = (asOfDateValue: string | undefined, daysFilter: string) => {
    const asOfDate = parseLocalDate(asOfDateValue) ?? new Date();
    const normalizedAsOfDate = new Date(asOfDate.getFullYear(), asOfDate.getMonth(), asOfDate.getDate());
    const currentWeekStart = startOfWeekMonday(normalizedAsOfDate);
    const lookbackDays = Math.min(90, Math.max(30, Number(daysFilter) || 30));
    const lookbackWeeks = Math.ceil(lookbackDays / 7);
    const monthWindowStart = addDays(currentWeekStart, -lookbackWeeks * 7);
    const windowEndSunday = addDays(currentWeekStart, lookbackWeeks * 7 + 6);

    return {
        asOfDate: normalizedAsOfDate,
        currentWeekStart,
        monthWindowStart,
        endOfNextMonth: windowEndSunday,
        lookbackWeeks,
    };
};

const findActualBalanceOnOrBefore = (dailyTrend: any[], targetDate: Date): number | null => {
    let actualBalance: number | null = null;
    for (const point of dailyTrend ?? []) {
        const pointDate = parseLocalDate(point?.date);
        if (!pointDate) continue;
        if (pointDate <= targetDate) {
            const balance = Number(point?.balance ?? 0);
            actualBalance = Number.isFinite(balance) ? balance : actualBalance;
        } else {
            break;
        }
    }
    return actualBalance;
};

const buildCurrentNextMonthSeries = (
    asOfDateValue: string | undefined,
    forecastWeeks: CashOutlook13wRecord[],
    weeklyActualInput: any,
    dailyTrendInput: any,
    daysFilter: string = '30',
): CashOutlook8wRecord[] => {
    const lookbackDays = Math.min(90, Math.max(30, Number(daysFilter) || 30));
    const weeklyKey = lookbackDays >= 90 ? '90' : lookbackDays >= 60 ? '60' : '30';
    const weeklyActual = Array.isArray(weeklyActualInput)
        ? weeklyActualInput
        : Array.isArray(weeklyActualInput?.[weeklyKey])
            ? weeklyActualInput[weeklyKey]
            : Array.isArray(weeklyActualInput?.['90'])
                ? weeklyActualInput['90']
                : [];
    const dailyTrend = Array.isArray(dailyTrendInput) ? dailyTrendInput : [];
    if (forecastWeeks.length === 0 && weeklyActual.length === 0) return [];

    const useScaledWindow = daysFilter === '60' || daysFilter === '90';
    const window = useScaledWindow
        ? getScaledWindow(asOfDateValue, daysFilter)
        : getCurrentNextMonthWindow(asOfDateValue);
    const actualByWeekStart = new Map(
        weeklyActual
            .filter((week: any) => week?.week_start)
            .map((week: any) => [week.week_start, week])
    );
    const overlapsWindow = (weekStartValue?: string | null, weekEndValue?: string | null) => {
        const weekStart = parseLocalDate(weekStartValue ?? undefined);
        const weekEnd = parseLocalDate(weekEndValue ?? undefined);
        if (!weekStart || !weekEnd) return false;
        return weekEnd >= window.monthWindowStart && weekStart <= window.endOfNextMonth;
    };

    const historical: CashOutlook8wRecord[] = [];
    for (let cursor = new Date(window.monthWindowStart); cursor < window.currentWeekStart; cursor = addDays(cursor, 7)) {
        const weekStartKey = formatDateKey(cursor);
        const syntheticWeekEnd = addDays(cursor, 6);
        const actualWeek = actualByWeekStart.get(weekStartKey) as { week_label?: string; week_start?: string; week_end?: string; cash_in?: number; cash_out?: number } | undefined;
        const week: { week_label: string; week_start: string; week_end: string; cash_in: number; cash_out: number } = actualWeek
            ? {
                week_label: actualWeek.week_label ?? formatWeekRange(weekStartKey, formatDateKey(syntheticWeekEnd)),
                week_start: actualWeek.week_start ?? weekStartKey,
                week_end: actualWeek.week_end ?? formatDateKey(syntheticWeekEnd),
                cash_in: Number(actualWeek.cash_in ?? 0),
                cash_out: Number(actualWeek.cash_out ?? 0),
            }
            : {
                week_label: formatWeekRange(weekStartKey, formatDateKey(syntheticWeekEnd)),
                week_start: weekStartKey,
                week_end: formatDateKey(syntheticWeekEnd),
                cash_in: 0,
                cash_out: 0,
            };
        const weekEnd = parseLocalDate(week.week_end);
        const actualBalance = weekEnd ? findActualBalanceOnOrBefore(dailyTrend, weekEnd) : null;

        // Historical weeks: actuals + projected from cashflow_forecast_snapshots (backend populates via cash_outlook_current_next_month_v1)
        historical.push({
            week_label: week.week_label,
            week_start: week.week_start,
            week_ending: week.week_end,
            actual_inflow: week.cash_in,
            actual_outflow: week.cash_out,
            actual_balance: actualBalance,
            projected_inflow: null,
            projected_outflow: null,
            projected_balance: null,
            projected_receipts: null,
            projected_fixed_outflows: null,
            projected_flexible_outflows: null,
            projected_discretionary_outflows: null,
            projected_net_cash: null,
            projected_beginning_balance: null,
            confidence: null,
            type: 'historical',
        });
    }

    const currentAndFuture = forecastWeeks
        .filter((week) => overlapsWindow(week?.week_start, week?.week_ending))
        .filter((week) => {
            const weekStart = parseLocalDate(week?.week_start);
            return !!weekStart && weekStart >= window.currentWeekStart;
        })
        .map((week) => {
            const weekStart = parseLocalDate(week?.week_start);
            const weekEnd = parseLocalDate(week?.week_ending);
            const actualWeek = week?.week_start ? (actualByWeekStart.get(week.week_start) as { cash_in?: number; cash_out?: number } | undefined) : null;
            const pointType: CashOutlook8wRecord['type'] = weekStart && weekEnd
                ? (weekEnd < window.asOfDate ? 'historical' : (weekStart <= window.asOfDate && window.asOfDate <= weekEnd ? 'current' : 'future'))
                : 'future';
            const actualBalance = weekEnd && pointType !== 'future'
                ? findActualBalanceOnOrBefore(dailyTrend, weekEnd < window.asOfDate ? weekEnd : window.asOfDate)
                : null;

            return {
                week_label: week?.week_label ?? '',
                week_start: week?.week_start ?? null,
                week_ending: week?.week_ending ?? '',
                actual_inflow: pointType !== 'future' ? Number(actualWeek?.cash_in ?? 0) : null,
                actual_outflow: pointType !== 'future' ? Number(actualWeek?.cash_out ?? 0) : null,
                actual_balance: actualBalance,
                projected_inflow: week?.receipts ?? null,
                projected_outflow: (week?.fixed_outflows ?? 0) + (week?.flexible_outflows ?? 0) + (week?.discretionary_outflows ?? 0),
                projected_balance: pointType === 'future' ? (week?.ending_cash ?? null) : null,
                projected_receipts: week?.receipts ?? null,
                projected_fixed_outflows: week?.fixed_outflows ?? null,
                projected_flexible_outflows: week?.flexible_outflows ?? null,
                projected_discretionary_outflows: week?.discretionary_outflows ?? null,
                projected_net_cash: week?.net_cash ?? null,
                projected_beginning_balance: week?.beginning_cash ?? null,
                confidence: week?.confidence ?? null,
                type: pointType,
            };
        });

    const deduped = new Map<string, CashOutlook8wRecord>();
    [...historical, ...currentAndFuture]
        .sort((left, right) => (left.week_ending || '').localeCompare(right.week_ending || ''))
        .forEach((point) => {
            if (point.week_ending) {
                deduped.set(point.week_ending, point);
            }
        });

    return Array.from(deduped.values());
};

const getNiceStep = (rawStep: number): number => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;

    if (normalized <= 1) return magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
};

const buildEvenTicks = (
    values: number[],
    options?: { tickCount?: number; includeZero?: boolean }
): { ticks: number[]; domain: [number, number] } => {
    const tickCount = Math.max(options?.tickCount ?? 5, 2);
    const includeZero = options?.includeZero ?? false;
    const numericValues = values.filter((value) => Number.isFinite(value));

    if (numericValues.length === 0) {
        const fallbackTicks = includeZero ? [0, 25, 50, 75, 100] : [0, 1];
        return {
            ticks: fallbackTicks,
            domain: [fallbackTicks[0], fallbackTicks[fallbackTicks.length - 1]],
        };
    }

    let minValue = Math.min(...numericValues);
    let maxValue = Math.max(...numericValues);

    if (includeZero) {
        minValue = Math.min(0, minValue);
        maxValue = Math.max(0, maxValue);
    }

    if (minValue === maxValue) {
        const base = Math.abs(maxValue) || 1;
        const step = getNiceStep(base / (tickCount - 1));
        const start = includeZero ? Math.min(0, minValue) : minValue - step * Math.floor((tickCount - 1) / 2);
        const ticks = Array.from({ length: tickCount }, (_, index) => start + step * index);
        return {
            ticks,
            domain: [ticks[0], ticks[ticks.length - 1]],
        };
    }

    const step = getNiceStep((maxValue - minValue) / (tickCount - 1));
    const domainMin = Math.floor(minValue / step) * step;
    const domainMax = Math.ceil(maxValue / step) * step;
    const ticks: number[] = [];

    for (let value = domainMin; value <= domainMax + step * 0.001; value += step) {
        ticks.push(Number(value.toFixed(10)));
    }

    return {
        ticks,
        domain: [ticks[0], ticks[ticks.length - 1]],
    };
};

export default function Charts({
    forecastData,
    cashData,
    cashOutlookData,
    topExpensesData,
    cashSourcesData,
    rawData,
    daysFilter = '30',
    businessId,
}: ChartsProps) {
    const [selectedTrendWindow, setSelectedTrendWindow] = useState<{
        title: string;
        startDate: string;
        endDate: string;
        totalAmount: number;
        initialFilters: Partial<TransactionFilterParams>;
    } | null>(null);
    const [selectedVendorWindow, setSelectedVendorWindow] = useState<{
        title: string;
        startDate: string;
        endDate: string;
        totalAmount: number;
        initialFilters: Partial<TransactionFilterParams>;
    } | null>(null);

    const formatCurrency = (value: number) => {
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
        }
        if (value >= 1000) {
            return `$${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
        }
        return `$${value}`;
    };

    const getReferenceDate = (): Date => {
        const asOf = parseLocalDate(rawData?.as_of_date);
        if (asOf) return asOf;
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    };

    const buildDateRange = (lookbackDays: number, endDate = getReferenceDate()) => {
        const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        const start = addDays(end, -(lookbackDays - 1));
        return {
            startDate: formatDateKey(start),
            endDate: formatDateKey(end),
        };
    };

    const openVendorDrilldown = ({
        title,
        totalAmount,
        vendorName,
        vendorExact,
        search,
        lookbackDays,
        omitStartDate,
    }: {
        title: string;
        totalAmount: number;
        vendorName?: string;
        vendorExact?: boolean;
        search?: string;
        lookbackDays: number;
        omitStartDate?: boolean;
    }) => {
        const { startDate, endDate } = buildDateRange(lookbackDays);
        setSelectedVendorWindow({
            title,
            startDate: omitStartDate ? '' : startDate,
            endDate,
            totalAmount,
            initialFilters: {
                direction: 'outflow',
                operating_flag: true,
                ...(vendorName ? { vendor_name: vendorName } : {}),
                ...(vendorName && vendorExact ? { vendor_exact: true } : {}),
                ...(search ? { search } : {}),
            },
        });
    };

    // Cash in vs Cash out

    // Cash Sources 
    // Filter out zeros for the pie itself
    const pieData = (cashSourcesData ?? []).filter(d => d.value > 0);

    // Recompute totals/top on filtered data (for chart + tooltip)
    const total = pieData.reduce((sum, s) => sum + s.value, 0);

    // If all values are zero, avoid rendering the pie
    const hasData = total > 0;

    // New backend shapes (prefer rawData when present)
    const cashInVsOutWeeklyRaw = rawData?.cash_in_vs_out_weekly;
    const cashInVsOutWeekly = Array.isArray(cashInVsOutWeeklyRaw) ? cashInVsOutWeeklyRaw : [];
    const currentNextMonthRaw = rawData?.cash_outlook_current_next_month_v1 ?? [];
    const cashOutlook13wRaw = rawData?.cash_outlook_13w_unconstrained ?? cashOutlookData;
    const cashOutlook13w = Array.isArray(cashOutlook13wRaw) ? cashOutlook13wRaw : [];
    const primaryCashOutlook = Array.isArray(currentNextMonthRaw) ? currentNextMonthRaw : [];
    // Forecast chart: always 30-day scale (fixed ~4 weeks history + ~4 weeks forecast). Days filter (60/90) does not affect chart scale.
    const forecastDaysFilter = '30';
    let rebuiltCurrentNextMonth = buildCurrentNextMonthSeries(
        rawData?.as_of_date,
        cashOutlook13w,
        rawData?.cash_in_vs_out_weekly,
        rawData?.daily_trend_90d,
        forecastDaysFilter,
    );
    const useExtendedWindow = false;
    if (useExtendedWindow && primaryCashOutlook.length > 0) {
        const primaryByWeekEnding = new Map(
            primaryCashOutlook
                .filter((p) => p.week_ending && p.projected_balance != null && Number.isFinite(p.projected_balance))
                .map((p) => [p.week_ending, p])
        );
        rebuiltCurrentNextMonth = rebuiltCurrentNextMonth.map((point) => {
            if (point.type !== 'historical') return point;
            const primary = primaryByWeekEnding.get(point.week_ending);
            if (!primary) return point;
            return {
                ...point,
                projected_inflow: primary.projected_inflow ?? point.projected_inflow,
                projected_outflow: primary.projected_outflow ?? point.projected_outflow,
                projected_balance: primary.projected_balance ?? point.projected_balance,
                projected_receipts: primary.projected_receipts ?? point.projected_receipts,
                projected_fixed_outflows: primary.projected_fixed_outflows ?? point.projected_fixed_outflows,
                projected_flexible_outflows: primary.projected_flexible_outflows ?? point.projected_flexible_outflows,
                projected_discretionary_outflows: primary.projected_discretionary_outflows ?? point.projected_discretionary_outflows,
                projected_net_cash: primary.projected_net_cash ?? point.projected_net_cash,
                projected_beginning_balance: primary.projected_beginning_balance ?? point.projected_beginning_balance,
                confidence: primary.confidence ?? point.confidence,
            };
        });
    }
    const hasPrimaryWithProjected = primaryCashOutlook.some(
        (p) => (p.type === 'historical' || p.type === 'current') && p.projected_balance != null && Number.isFinite(p.projected_balance)
    );
    const shouldUseRebuiltCurrentNextMonth =
        rebuiltCurrentNextMonth.length > 0 &&
        (useExtendedWindow || primaryCashOutlook.length === 0 || !hasPrimaryWithProjected);
    const forecastWeekCount = 8;
    const fallbackFrom13w: CashOutlook8wRecord[] = cashOutlook13w.slice(0, forecastWeekCount).map((week: any) => ({
        week_label: week.week_label,
        week_start: week.week_start,
        week_ending: week.week_ending,
        actual_inflow: null,
        actual_outflow: null,
        actual_balance: null,
        projected_inflow: week.receipts ?? 0,
        projected_outflow: (week.fixed_outflows ?? 0) + (week.flexible_outflows ?? 0) + (week.discretionary_outflows ?? 0),
        projected_balance: week.ending_cash ?? 0,
        type: 'future',
    }));
    const cashOutlookSeries: CashOutlook8wRecord[] = shouldUseRebuiltCurrentNextMonth
        ? rebuiltCurrentNextMonth
        : primaryCashOutlook.length > 0
            ? primaryCashOutlook
            : fallbackFrom13w;
    const calendarItems = rawData?.cashflow_calendar ?? [];
    const unconstrainedByWeekEnding = new Map(
        cashOutlook13w.map((week: any) => [week.week_ending, week])
    );
    const sortedByWeek = [...cashOutlookSeries].sort((a, b) =>
        (a.week_ending || '').localeCompare(b.week_ending || '')
    );
    let runningBalance: number | null = null;
    const cashOutlookChartData = sortedByWeek.map((point: CashOutlook8wRecord) => {
        const unconstrainedWeek = unconstrainedByWeekEnding.get(point.week_ending) || {};
        const netCash = point.projected_net_cash ?? unconstrainedWeek.net_cash ?? 0;
        const hasActual = point.actual_balance != null && Number.isFinite(point.actual_balance);
        const isCurrent = point.type === 'current';
        const isFuture = point.type === 'future';
        const hasProjectedFromBackend = point.projected_balance != null && Number.isFinite(point.projected_balance);

        if (hasActual) runningBalance = point.actual_balance;

        let projectedBalance: number | null = null;
        if (point.type === 'historical' && hasProjectedFromBackend) {
            // Keep projected from cashflow_forecast_snapshots (backend)
            projectedBalance = point.projected_balance;
        } else if (isCurrent && hasActual) {
            projectedBalance = point.actual_balance;
        } else if (isFuture) {
            if (runningBalance != null) {
                runningBalance = runningBalance + Number(netCash || 0);
                projectedBalance = runningBalance;
            } else {
                projectedBalance = point.projected_balance ?? unconstrainedWeek.ending_cash ?? null;
            }
        } else if (hasProjectedFromBackend) {
            projectedBalance = point.projected_balance;
        }

        return {
            ...point,
            receipts: point.projected_receipts ?? unconstrainedWeek.receipts ?? point.projected_inflow ?? null,
            fixed_outflows: point.projected_fixed_outflows ?? unconstrainedWeek.fixed_outflows ?? null,
            flexible_outflows: point.projected_flexible_outflows ?? unconstrainedWeek.flexible_outflows ?? null,
            discretionary_outflows: point.projected_discretionary_outflows ?? unconstrainedWeek.discretionary_outflows ?? null,
            net_cash: netCash,
            beginning_cash: point.projected_beginning_balance ?? unconstrainedWeek.beginning_cash ?? null,
            ending_cash: unconstrainedWeek.ending_cash ?? point.projected_balance ?? null,
            confidence: point.confidence ?? unconstrainedWeek.confidence ?? null,
            projected_balance: projectedBalance,
        };
    });
    const renderedCashOutlookData = cashOutlookChartData;

    // Trend Series (Cash In vs Cash Out)
    // Use the explicit cash_in_cash_out daily data for the selected period
    // If not found, fall back to weekly.
    const selectedDailyCashRaw = cashData?.[daysFilter as "30" | "60" | "90"] || cashData?.["30"];
    const selectedDailyCash = Array.isArray(selectedDailyCashRaw) ? selectedDailyCashRaw : [];
    const trendSeries = (selectedDailyCash.length > 0)
        ? selectedDailyCash
        : cashInVsOutWeekly;

    const trendIsDaily = trendSeries.length > 0 && !!trendSeries[0].date;

    const buildTrendWindow = (point: any) => {
        const startDate = point?.week_start || point?.date;
        if (!startDate) return null;
        const start = parseLocalDate(startDate);
        if (!start) return null;

        const endDate = point?.week_end || point?.week_ending || formatDateKey(addDays(start, 6));
        const cashIn = Number(point?.cash_in ?? point?.cashIn ?? 0);
        const cashOut = Number(point?.cash_out ?? point?.cashOut ?? 0);

        return {
            title: `Cash Flow Trend · ${formatWeekRange(startDate, endDate) || startDate}`,
            startDate,
            endDate,
            totalAmount: cashIn + cashOut,
            initialFilters: {
                operating_flag: true,
            },
        };
    };

    const handleTrendClick = (chartState: any) => {
        const point = chartState?.activePayload?.[0]?.payload;
        const window = buildTrendWindow(point);
        if (window) {
            setSelectedTrendWindow(window);
        }
    };

    const cashTrendValues = trendSeries.flatMap((point: any) => {
        const cashIn = trendIsDaily ? (point.cash_in ?? point.cashIn) : point.cash_in;
        const cashOut = trendIsDaily ? (point.cash_out ?? point.cashOut) : point.cash_out;
        return [cashIn ?? 0, cashOut ?? 0];
    });
    const cashTrendAxis = buildEvenTicks(cashTrendValues, { tickCount: 5, includeZero: true });

    const cashOutlookValues = renderedCashOutlookData.flatMap((point: CashOutlook8wRecord & { ending_cash?: number | null; beginning_cash?: number | null }) => [
        point.actual_balance ?? null,
        point.projected_balance ?? null,
        point.beginning_cash ?? null,
        point.ending_cash ?? null,
    ]).filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    const cashOutlookAxis = buildEvenTicks(cashOutlookValues, { tickCount: 5, includeZero: true });

    return (
        <div className="flex flex-wrap md:grid md:grid-cols-2 gap-4">

            {/* CASH IN VS CASH OUT */}
            <div data-tutorial="dashboard-cash-flow-trend" className="dashboard-card relative">
                <h2 className="dashboard-header">Cash Flow Trend</h2>
                {/* <div className="space-x-2 absolute top-4 right-4">
                    {[30].map((d) => (
                        <button
                            key={d}
                            className={`px-2 py-1 text-sm rounded ${range === d
                                ? 'bg-indigo-400 text-white'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                }`}
                            onClick={() => setRange(d as 30)}
                        >
                            {d}d
                        </button>
                    ))}
                </div> */}

                <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                        data={trendSeries}
                        margin={{ top: 12, bottom: 5, right: 24 }}
                        barCategoryGap="30%"
                        onClick={handleTrendClick}
                    >
                        <CartesianGrid vertical={false} horizontal stroke="#e5e7eb" strokeDasharray="0" />

                        <XAxis
                            dataKey={trendIsDaily ? 'date' : 'week_label'}
                            type="category"
                            minTickGap={5}
                            interval="preserveStartEnd"
                            tickFormatter={(value) => {
                                if (trendIsDaily) {
                                    return parseLocalDate(value)?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }) ?? '';
                                }
                                return String(value);
                            }}
                            tick={{ fontSize: 14 }}
                            tickMargin={14}
                            axisLine={false}
                            tickLine={false}
                        />

                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatCurrency}
                            tick={{ fontSize: 14, textAnchor: 'start', dx: -70 }}
                            width={80}
                            interval={0}
                            allowDecimals={false}
                            ticks={cashTrendAxis.ticks}
                            domain={cashTrendAxis.domain}
                        />

                        <Tooltip
                            cursor={{ fill: '#f3f4f6' }}
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                        <div className="rounded-md bg-white p-3 shadow-md border text-sm text-gray-700 space-y-1">
                                            <div className="font-semibold">{trendIsDaily ? (parseLocalDate(label)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) ?? '') : String(label)}</div>
                                            <div>Cash In: <span className="font-medium text-[#9798FC]">${(data.cash_in ?? data.cashIn ?? 0).toLocaleString()}</span></div>
                                            <div>Cash Out: <span className="font-medium">${(data.cash_out ?? data.cashOut ?? 0).toLocaleString()}</span></div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />

                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            iconType="circle"
                            iconSize={10}
                            wrapperStyle={{ paddingTop: '20px', fontSize: '14px' }}
                        />

                        <Bar
                            dataKey={trendIsDaily ? (trendSeries[0].cash_in !== undefined ? 'cash_in' : 'cashIn') : 'cash_in'}
                            fill="#9798FC"
                            fillOpacity={0.8}
                            radius={[4, 4, 0, 0]}
                            name="Cash In"
                        />

                        <Bar
                            dataKey={trendIsDaily ? (trendSeries[0].cash_out !== undefined ? 'cash_out' : 'cashOut') : 'cash_out'}
                            fill="#6b7280"
                            fillOpacity={0.8}
                            radius={[4, 4, 0, 0]}
                            name="Cash Out"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* CASH OUTLOOK */}
            <div data-tutorial="dashboard-cash-outlook" className="dashboard-card">
                <h2 className="dashboard-header">
                    Cash Outlook
                </h2>

                {/* {forecastSnapshotAgeDays > 0 && (
                    <div className="mb-3 text-xs text-gray-500">
                        Forecast snapshot from {forecastSnapshotAgeDays} day{forecastSnapshotAgeDays === 1 ? '' : 's'} ago. Actuals are newer.
                    </div>
                )} */}

                {!renderedCashOutlookData.length ? (
                    <div className="h-[200px] flex items-center justify-center text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        We’ll show this once we have enough data.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={renderedCashOutlookData} margin={{ top: 12, right: 24 }}>
                            <defs>
                                <linearGradient id="cashOutlookActualFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#A496FF" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="#A496FF" stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="cashOutlookProjectedFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#1F2937" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="#1F2937" stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid vertical={false} horizontal stroke="#e5e7eb" strokeDasharray="0" />

                            <XAxis
                                dataKey="week_start"
                                axisLine={false}
                                tickLine={false}
                                tickMargin={14}
                                tick={{ fontSize: 14 }}
                                tickFormatter={(value, index) => {
                                    const point = renderedCashOutlookData[index];
                                    return formatCashOutlookTick(point, value);
                                }}
                            />

                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatCurrency}
                                tick={{ fontSize: 14, textAnchor: 'start', dx: -70 }}
                                width={80}
                                interval={0}
                                allowDecimals={false}
                                ticks={cashOutlookAxis.ticks}
                                domain={cashOutlookAxis.domain}
                            />

                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        const showProjectedDetails = !(data.type === 'current' && data.actual_balance !== null && data.actual_balance !== undefined);
                                        return (
                                            <div className="rounded-md bg-white p-3 shadow-lg border text-sm text-gray-700 space-y-2 min-w-[220px]">
                                                <div className="font-bold text-gray-900 border-b pb-1">
                                                    {formatCashOutlookTooltipTitle(data, label)}
                                                </div>
                                                <div className="space-y-1">
                                                    {data.actual_balance !== null && data.actual_balance !== undefined && (
                                                        <div className="space-y-1 pb-2 border-b">
                                                            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-400">
                                                                {data.type === 'current' ? 'Actual (to date)' : 'Actual'}
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span>Inflow</span>
                                                                <span className="font-medium text-indigo-400">{formatCurrency(data.actual_inflow || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span>Outflow</span>
                                                                <span className="font-medium text-slate-700">{formatCurrency(data.actual_outflow || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span>Balance</span>
                                                                <span className="font-semibold text-gray-900">{formatCurrency(data.actual_balance || 0)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {showProjectedDetails && data.projected_balance !== null && data.projected_balance !== undefined && (data.type === 'future' || data.type === 'current') && (
                                                        <div className="space-y-1">
                                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Projected</div>
                                                            <div className="flex justify-between gap-4">
                                                                <span>Inflow</span>
                                                                <span className="font-medium text-indigo-400">{formatCurrency(data.projected_inflow || data.receipts || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span>Outflow</span>
                                                                <span className="font-medium text-slate-700">{formatCurrency(data.projected_outflow || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between gap-4">
                                                                <span>Balance</span>
                                                                <span className="font-semibold text-gray-900">{formatCurrency(data.projected_balance || 0)}</span>
                                                            </div>
                                                            {/* {data.confidence && (
                                                                <div className="flex justify-between gap-4">
                                                                    <span>Confidence</span>
                                                                    <span className="font-medium capitalize">{data.confidence}</span>
                                                                </div>
                                                            )} */}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-gray-400 italic text-right uppercase tracking-tighter">
                                                    {String(data.type || 'future')} period
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />


                            {/* Projected first (drawn underneath) so Actual appears on top */}
                            <Area
                                type="monotone"
                                dataKey="actual_balance"
                                stroke="#9798FC"
                                strokeWidth={1}
                                fill="url(#cashOutlookActualFill)"
                                fillOpacity={1}
                                dot={false}
                                activeDot={{ r: 5 }}
                                connectNulls
                                name="Actual"
                            />

                            <Area
                                type="monotone"
                                dataKey="projected_balance"
                                stroke="#697079"
                                strokeWidth={1}
                                fill="url(#cashOutlookProjectedFill)"
                                fillOpacity={1}
                                dot={false}
                                activeDot={{ r: 5 }}
                                connectNulls
                                name="Projected"
                            />

                            <Line
                                type="monotone"
                                dataKey="actual_balance"
                                stroke="#6366f1"
                                strokeWidth={2.5}
                                dot={false}
                                activeDot={false}
                                connectNulls
                                name="Actual Balance"
                            />

                            <Line
                                type="monotone"
                                dataKey="projected_balance"
                                stroke="#6b7280"
                                strokeWidth={2}
                                dot={false}
                                activeDot={false}
                                connectNulls
                                name="Projected Balance"
                            />
                            <Legend
                                verticalAlign="bottom"
                                align="center"
                                iconType="circle"
                                iconSize={10}
                                wrapperStyle={{ paddingTop: '20px', fontSize: '14px' }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* TOP EXPENSES */}
            <div className="dashboard-card">
                <h2 className="dashboard-header">Top Expenses</h2>

                {(() => {
                    const periodKey = `last_${daysFilter}d` as keyof TopExpensesResponse;
                    const expenses = topExpensesData?.[periodKey] || [];
                    return expenses.length === 0 ? (
                        <div className="h-[200px] flex items-center justify-center text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                            No expense data available for this period.
                        </div>
                    ) : (
                        <div className="mt-2">
                            {expenses.slice(0, 5).map((expense, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => openVendorDrilldown({
                                        title: `Top Expenses · ${expense.vendor_name}`,
                                        totalAmount: expense.amount ?? 0,
                                        vendorName: expense.vendor_name,
                                        vendorExact: true,
                                        lookbackDays: Number(daysFilter) || 30,
                                    })}
                                    className="flex w-full justify-between items-center border-b py-2 px-2 last:border-0 transition-colors min-h-[56px] hover:bg-gray-50 cursor-pointer text-left"
                                >
                                    {/* Left Side: Name and Category on one line */}
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="text-sm font-[17px] font-medium text-gray-900 truncate max-w-[150px] sm:max-w-none">
                                            {expense.vendor_name}
                                        </div>
                                        <span className={`inline-flex items-center justify-center min-w-[44px] h-[19px] gap-1 rounded-[4px] px-2 text-[10px] font-medium leading-[10px] text-center font-inter antialiased shrink-0 ${getCategoryColor(expense.category)}`}>
                                            <span className="capitalize">{expense.category}</span>
                                        </span>
                                    </div>

                                    {/* Right Side: Just the Amount */}
                                    <div className="text-right shrink-0">
                                        <div className="text-sm font-semibold text-[#4C545F]">
                                            ${expense.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    );
                })()}
            </div>


            {/* UPCOMING PAYABLES */}
            <div className="dashboard-card">
                <h2 className="dashboard-header">Upcoming Payments</h2>
                <div className="mt-4">
                    {calendarItems.length === 0 && (
                        <div className="text-sm text-gray-500 p-4">We'll show this once we have enough data.</div>
                    )}
                    {calendarItems
                        .sort((a: any, b: any) => {
                            const dateA = parseLocalDate(a.expected_week || '9999-12-31')?.getTime() ?? Number.MAX_SAFE_INTEGER;
                            const dateB = parseLocalDate(b.expected_week || '9999-12-31')?.getTime() ?? Number.MAX_SAFE_INTEGER;
                            return dateA - dateB; // Ascending order (earliest first)
                        })
                        .slice(0, 5)
                        .map((item: any, idx: number) => {
                            const vendorName = item.vendor_name || item.name || item.driver;
                            const vendorLabel = vendorName || item.category || 'Item';

                            // Format date to MM-DD-YYYY
                            const formatDate = (dateStr: string) => {
                                if (!dateStr) return '';
                                const date = parseLocalDate(dateStr);
                                if (!date) return '';
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${month}/${day}/${year}`;
                            };

                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                        openVendorDrilldown({
                                            title: `Upcoming Payments · ${vendorLabel}`,
                                            totalAmount: Number(item.amount ?? item.value ?? 0),
                                            vendorName,
                                            vendorExact: Boolean(vendorName),
                                            search: vendorName ? undefined : vendorLabel,
                                            lookbackDays: 90,
                                            omitStartDate: true,
                                        });
                                    }}
                                    className="flex w-full justify-between items-center border-b py-2 px-2 last:border-0 transition-colors min-h-[56px] hover:bg-gray-50 cursor-pointer text-left"
                                >
                                    {/* Left Side: Name and Date tag */}
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate max-w-[150px] sm:max-w-none">
                                            {vendorLabel}
                                        </div>
                                        <span className="inline-flex items-center justify-center min-w-[44px] h-[19px] gap-1 rounded-[4px] px-2 text-[10px] font-medium leading-[10px] text-center font-inter antialiased shrink-0 bg-gray-100 text-gray-600">
                                            {formatDate(item.expected_week)}
                                        </span>
                                    </div>

                                    {/* Right Side: Just the Amount */}
                                    <div className="text-right shrink-0">
                                        <div className="text-sm font-semibold text-[#4C545F]">
                                            ${(item.amount ?? item.value ?? 0).toLocaleString()}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                </div>
            </div>

            <WeeklyDrilldownModal
                isOpen={!!selectedTrendWindow}
                onClose={() => setSelectedTrendWindow(null)}
                title={selectedTrendWindow?.title || ''}
                startDate={selectedTrendWindow?.startDate || ''}
                endDate={selectedTrendWindow?.endDate || ''}
                startAmount={selectedTrendWindow?.totalAmount || 0}
                initialFilters={selectedTrendWindow?.initialFilters}
                businessId={businessId}
            />
            <WeeklyDrilldownModal
                isOpen={!!selectedVendorWindow}
                onClose={() => setSelectedVendorWindow(null)}
                title={selectedVendorWindow?.title || ''}
                startDate={selectedVendorWindow?.startDate || ''}
                endDate={selectedVendorWindow?.endDate || ''}
                startAmount={selectedVendorWindow?.totalAmount || 0}
                initialFilters={selectedVendorWindow?.initialFilters}
                businessId={businessId}
            />
        </div>
    );
}
